import React, { useEffect, useRef } from "react";
import { useUnmount } from "react-use";
import { t } from "locales";
import { updateVideoStream } from "lib/liveStreamHelpers";
import { ProduceStream } from "types";
import BaseIcon from "components/BaseIcon";
import NetworkQualityBadge from "components/NetworkQualityBadge";
import RippleAvatarAnimation from "../../../RippleAvatarAnimation";
import "./style.scss";
import TransitionControl from "../../../TransitionControl";

interface Props {
  item: ProduceStream;
  className?: string;
  mirrorReflection?: boolean;
  setPlayer?: React.Dispatch<React.SetStateAction<HTMLVideoElement | null>>;
  forceMute?: boolean;
  onClick?: () => void;
  showControl?: boolean;
}

export default function MainStreamView({
  item,
  setPlayer,
  className,
  mirrorReflection,
  forceMute,
  onClick,
  showControl = true,
}: Props) {
  const player = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    return updateVideoStream(player.current, item.stream);
  }, [item.stream, item.hasVideo, item.hasAudio, player]);

  useEffect(() => {
    setPlayer && setPlayer(player.current);
  }, [player, setPlayer]);

  const cls = ["main-stream-view"];
  if (!item.hasVideo) cls.push("__disabled-video");
  if (mirrorReflection) cls.push("__mirror-reflection");
  if (item.isSpeaking) cls.push("__speaking");
  if (className) cls.push(className);

  useUnmount(() => {
    player.current && (player.current.srcObject = null);
  });

  return (
    <div className={cls.join(" ")} onClick={onClick}>
      <video className="main-stream-view__video" ref={player} muted={forceMute} playsInline autoPlay />
      {!item.hasVideo && (
        <RippleAvatarAnimation className="main-stream-view__animation" size={60} user={item.user.user} />
      )}

      {!item.hasAudio && (
        <TransitionControl className="main-stream-view__muted-notify" showControl={showControl}>
          <BaseIcon className="main-stream-view__muted-ico" size={20}>
            mic_off
          </BaseIcon>
          <span>{t("Muted")}</span>
        </TransitionControl>
      )}

      {item.quality !== undefined && (
        <NetworkQualityBadge quality={item.quality} className="main-stream-view__network-badge" />
      )}
      {item.videoAutoPaused && (
        <div className="main-stream-view__video-paused" title="Video paused due to network">
          <BaseIcon size={20}>videocam_off</BaseIcon>
        </div>
      )}
    </div>
  );
}
